import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { FormInput } from "go-form";
import useFlash from "go-alert/AlertMessage";
import ImageColorForm from "go-app/components/ImageForm/ImageColorForm";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { FormSelectGroup } from "go-form/components/FormSelect";
import { adminApi } from "../../../../../../../services/Api/Admin/adminApi";
import { ApplicationTemplateApi } from "../../../../../../../services/Api/Admin/adminTypes";

interface Props {
	app?: ApplicationTemplateApi;
}

const AppForm = ({ app }: Props) => {
	const form = useForm<ApplicationTemplateApi>({
		criteriaMode: "all",
		defaultValues: { ...app, tags: app?.tags ? app.tags : [] },
	});
	const [newAvatar, setNewAvatar] = useState<string | undefined>(app?.logo_link?.default_link);
	const {
		register,
		handleSubmit,
		formState: { errors },
		setError,
		formState,
		setValue,
		control,
		getValues,
		reset,
		watch,
	} = form;
	const [loading, setLoading] = useState(false);
	const { t } = useTranslation("admin");
	const history = useHistory();
	const { addFlash, addSuccessFlash } = useFlash();

	const mapTags = (tags: string[]) => {
		return tags?.map((tag) => {
			return {
				value: tag,
				label: tag,
			};
		});
	};

	const fetchAppProviderTypes = async (search: string, options?: Record<string, any>) => {
		const res = await adminApi.getAppProviderTypes(options);
		return res
			.map((item) => ({
				value: item,
				label: t(`enums.apps.provider.${item}`),
			}))
			.filter((item) => item.label.toLowerCase().includes(search?.toLowerCase()));
	};

	const getAppTypes = () => {
		return [
			{ label: t("enums.apps.provider.GOPOS"), value: "GOPOS" },
			{ label: t("enums.apps.provider.GOSTOCK"), value: "GOSTOCK" },
			{ label: t("enums.apps.provider.GOORDER"), value: "GOORDER" },
			{ label: t("enums.apps.provider.GOCRM"), value: "GOCRM" },
		];
	};

	const handleImageChange = (imageBase: string | undefined) => {
		setNewAvatar(imageBase);
	};

	const onSubmit = handleSubmit(async (data: ApplicationTemplateApi) => {
		setLoading(true);
		if (getValues("logo.image_data") || !newAvatar) {
			data.logo_link = undefined;
		}
		try {
			if (app?.id) {
				const res = await adminApi.updateApp(data);
				setLoading(false);
				reset(res);
				addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
			} else {
				const res = await adminApi.createApp(data);
				history.push(`/admin/apps/${res.id}`);
				setLoading(false);
				addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
			}
		} catch (e) {
			setLoading(false);
			handleError.form(e, setError, addFlash);
		}
	});

	const onChangeTags = (values: string[]) => {
		setValue("tags", values);
	};
	const onCreateTag = (value: string) => {
		setValue("tags", [...getValues().tags, value], { shouldDirty: true });
	};

	return (
		<FormDirty
			formState={formState}
			key="app-form"
			loading={loading}
			noValidate
			isSubmitting={formState.isSubmitting}
			onSubmit={onSubmit}
		>
			<fieldset className={"form-group no-gutters"}>
				<h5>{t("common.word.basic_data", { ns: "lib" })}</h5>
				<div className={"row"}>
					<div className={"col-md-10"}>
						<div className={"row"}>
							<div className={"col-md-6"}>
								<FormSelectGroup
									label={t("lib:common.word.name")}
									name="provider"
									disabled={app?.id !== undefined}
									errors={errors}
									loadOptions={fetchAppProviderTypes}
									control={control}
									defaultValue={{
										value: app?.provider,
										label: app?.provider ? t(`enums.apps.provider.${app.provider}`) : "",
									}}
									data-testid="provider"
								/>
							</div>
							<div className={"col-md-6"}>
								<FormSelectGroup
									label={t("modules.app.field.application_type.title")}
									name="application_type"
									disabled={app?.id !== undefined}
									errors={errors}
									options={getAppTypes()}
									control={control}
									data-testid="application_type"
								/>
							</div>
							<div className={"col-md-6"}>
								<FormInput
									label={t("modules.app.field.owner.title")}
									register={register}
									name="owner"
									errors={errors}
								/>
							</div>
							<div className={"col-md-6"}>
								<FormInput
									label={t("modules.app.field.owner_link.title")}
									register={register}
									name="owner_link"
									errors={errors}
								/>
							</div>
							<div className={"col-md-6"}>
								<FormInput
									as={"textarea"}
									rows={3}
									label={t("modules.app.field.long_description.title")}
									register={register}
									name="long_description"
									errors={errors}
								/>
							</div>
							<div className={"col-md-6"}>
								<FormInput
									as={"textarea"}
									rows={3}
									label={t("modules.app.field.help_description.title")}
									register={register}
									name="help_description"
									errors={errors}
								/>
							</div>
							<div className={"col-md-6"}>
								<FormInput
									label={t("modules.app.field.short_description.title")}
									register={register}
									name="short_description"
									errors={errors}
								/>
							</div>
							<div className={"col-md-6"}>
								<FormInput
									label={t("modules.app.field.documentation_link.title")}
									register={register}
									name="documentation_link"
									errors={errors}
								/>
							</div>
							<div className={"col-md-6"}>
								<FormInput
									label={t("modules.app.field.rules_link.title")}
									register={register}
									name="rules_link"
									errors={errors}
								/>
							</div>
							<div className={"col-md-6"}>
								<FormInput
									label={t("modules.app.field.privacy_policy_link.title")}
									register={register}
									name="privacy_policy_link"
									errors={errors}
								/>
							</div>
							<div className={"col-md-12"}>
								<FormSelectGroup
									control={control}
									onCreateOption={onCreateTag}
									isMulti
									onChange={onChangeTags}
									loadOptions={() => mapTags(watch("tags"))}
									defaultValue={mapTags(watch("tags"))}
									errors={errors}
									name="tags"
									label={t("common.word.tags")}
									data-testid="tags"
								/>
							</div>
						</div>
					</div>
					<div className={"col-md-2"} style={{ minWidth: "150px" }}>
						<ImageColorForm
							form={form}
							handleSave={handleImageChange}
							prefix="logo"
							mode="IMAGE"
							data={app ?? {}}
							resourceLink={app?.logo_link}
							resourceData={app?.logo}
						/>
					</div>
				</div>
			</fieldset>
		</FormDirty>
	);
};

export default AppForm;

// t("enums.apps.provider.LOCATIONS")
// t("enums.apps.provider.GOHOTELINTEGRATION")
// t("enums.apps.provider.CALL_RECORDER")
// t("enums.apps.provider.NFHOTEL")
// t("enums.apps.provider.HOTELGRAM")
// t("enums.apps.provider.MEWS")
// t("enums.apps.provider.GOZONE")
// t("enums.apps.provider.HOTELLION")
// t("enums.apps.provider.HOLIDAY_PMS")
// t("enums.apps.provider.GOPOS_V2")
// t("enums.apps.provider.DOCUMENT_TRANSFER")
// t("enums.apps.provider.FILE_IMPORT")
// t("enums.apps.provider.GASTROSUPPLIER")
// t("enums.apps.provider.GASTROSUPPLIER_MERCHANT")
// t("enums.apps.provider.CHART")
// t("enums.apps.provider.KWHOTEL")
// t("enums.apps.provider.BETASI")
// t("enums.apps.provider.HOTRES")
// t("enums.apps.provider.GOCLIENT")
// t("enums.apps.provider.WOLT_DRIVE")
// t("enums.apps.provider.INFOR_STARLIGHT")
